import { v4 as uuidv4 } from 'uuid';
import { cloneDeep, omit } from 'lodash';
import { NodeType } from '@amplify/amp-module';
import { replaceStorageValue } from '@amplify/chatbot-core';

import {
  ChangeCreateAmpDto,
  SetAmps,
  SetCurrentAmp,
  SetCreateAmpDto,
  SetFavoriteAmps,
  SetAmpStep,
  ResetStepper,
  AddAmpToRecentlyViewed,
  ResetStore,
  SetLiveAmps,
  UpdateAmpData,
  SetAmpSortOrder,
  SetSearchedAmps,
  DeleteConversations,
  SetSelectedConversationId,
  ForceRebuild,
  SetTemplates,
  SetDraftAmps,
} from '@/constants/mutations';
import {
  GetCurrentAmp,
  GetOrganizationId,
  GetStorageEntries,
  GetCreateAmpDto,
  GetAmpStorage,
  GetAmpData,
  GetAmpSortOrder,
  GetStorageKeyValues,
  GetCurrentConversations,
  GetAvailableAccountKeys,
} from '@/constants/getters';
import {
  AddToFavorites,
  CreateAmp,
  DeleteAmpById,
  CopyAmpById,
  DeleteAmpFile,
  FetchAmpFiles,
  FetchAmps,
  FetchFavoriteAmps,
  GetAmpById,
  RemoveFromFavorites,
  UpdateAmp,
  UploadAmpFile,
  ClearStore,
  ResetMenu,
  GetRecentlyViewedAmps,
  FetchLiveAmps,
  SearchAmps,
  UpdateStorageValue,
  AddStorageValue,
  DeleteStorageValue,
  GetPublishedAmp,
  ChangeAmpSortOrder,
  ChangeLiveAmpSortOrder,
  ChangeFavoriteAmpSortOrder,
  FetchEDetailAmps,
  FetchTemplates,
  FetchDraftAmps,
  ChangeDraftAmpSortOrder,
} from '@/constants/actions';
import { DEFAULT_AMP_DTO, DEFAULT_AMP, RECENTLY_VIEWED_LENGTH, AMP_SORT_BY_OPTIONS } from '@/constants/amp';
import { MediaUploadFailed } from '@/constants/notifications';
import { endpoints } from '~/services/amplify-api/endpoints';
import { buttonsToValues } from '@/utils/builder-utils';
import {
  updateProperties,
  insertElement,
  createConversationObject,
  defaultMenuState,
  objectToSortedArray,
  removeElement,
} from '@/utils/amp-utils';

const defaultState = () => {
  return {
    amps: [],
    draftAmps: [],
    favoriteAmps: [],
    liveAmps: [],
    searchedAmps: [],
    templates: [],
    recentlyViewedAmps: {},
    totalAmps: 0,
    totalDraftAmps: 0,
    totalFavoriteAmps: 0,
    totalLiveAmps: 0,
    totalSearchedAmps: 0,
    currentAmp: DEFAULT_AMP,
    createAmpDto: DEFAULT_AMP_DTO,
    sortOrder: AMP_SORT_BY_OPTIONS[0],
  };
};

export const state = defaultState;

export const mutations = {
  [ResetStore](state) {
    Object.assign(state, {
      ...defaultState(),
      recentlyViewedAmps: state.recentlyViewedAmps,
    });
  },

  [SetAmps](state, payload) {
    const { data, total } = payload || {};
    state.amps = data || [];
    state.totalAmps = total || 0;
  },

  [SetDraftAmps](state, payload) {
    const { data, total } = payload || {};
    state.draftAmps = data || [];
    state.totalDraftAmps = total || 0;
  },

  [SetSearchedAmps](state, payload) {
    const { data, total } = payload || {};
    state.searchedAmps = data || [];
    state.totalSearchedAmps = total || 0;
  },

  [SetLiveAmps](state, payload) {
    const { data, total } = payload || {};
    state.liveAmps = data || [];
    state.totalLiveAmps = total || 0;
  },

  [SetFavoriteAmps](state, payload) {
    const { data, total } = payload || {};
    state.favoriteAmps = data || [];
    state.totalFavoriteAmps = total || 0;
  },

  [SetTemplates](state, payload) {
    state.templates = payload;
  },

  [SetAmpStep](state, step) {
    state.step = step;
  },

  [ResetStepper](state) {
    state.createAmpDto = DEFAULT_AMP_DTO;
  },

  [ChangeCreateAmpDto](state, data) {
    state.createAmpDto = { ...state.createAmpDto, ...data };
  },

  [DeleteAmpById](state, id) {
    const { amps, draftAmps, favoriteAmps, liveAmps, searchedAmps } = state;

    const index = amps.findIndex((amp) => amp.id === id);
    const draftIndex = draftAmps.findIndex((amp) => amp.id === id);
    const favoriteIndex = favoriteAmps.findIndex((amp) => amp.id === id);
    const liveIndex = liveAmps.findIndex((amp) => amp.id === id);
    const searchedIndex = searchedAmps.findIndex((amp) => amp.id === id);

    const amp = (index !== -1 && { ...amps[index] }) ||
      (draftIndex !== -1 && { ...draftAmps[draftIndex] }) ||
      (favoriteIndex !== -1 && { ...favoriteAmps[favoriteIndex] }) ||
      (liveIndex !== -1 && { ...liveAmps[liveIndex] }) ||
      (searchedIndex !== -1 && { ...searchedAmps[searchedIndex] }) || { ...state.currentAmp };

    if (!amp) {
      return;
    }

    if (index !== -1) {
      state.amps = amps.filter((_, i) => i !== index);
      state.totalAmps -= 1;
    }

    if (draftIndex !== -1) {
      state.draftAmps = draftAmps.filter((_, i) => i !== draftIndex);
      state.totalDraftAmps -= 1;
    }

    if (favoriteIndex !== -1) {
      state.favoriteAmps = favoriteAmps.filter((_, i) => i !== favoriteIndex);
      state.totalFavoriteAmps -= 1;
    }

    if (liveIndex !== -1) {
      state.liveAmps = liveAmps.filter((_, i) => i !== liveIndex);
      state.totalLiveAmps -= 1;
    }

    if (searchedIndex !== -1) {
      state.searchedAmps = searchedAmps.filter((_, i) => i !== searchedIndex);
      state.totalSearchedAmps -= 1;
    }

    const { organizationId } = amp || {};

    if (organizationId) {
      const recentAmps = [...(state.recentlyViewedAmps[organizationId] || [])];
      const recentlyViewedIndex = recentAmps.findIndex((amp) => amp.id === id);

      if (recentlyViewedIndex !== -1) {
        recentAmps.splice(recentlyViewedIndex, 1);
        state.recentlyViewedAmps[organizationId] = recentAmps;
      }
    }
  },

  [UpdateAmp]: (state, data) => {
    const id = data?.id || state.currentAmp?.id;

    const amps = [...state.amps];
    const draftAmps = [...state.draftAmps];
    const favoriteAmps = [...state.favoriteAmps];
    const liveAmps = [...state.liveAmps];
    const searchedAmps = [...state.searchedAmps];

    const index = amps.findIndex((amp) => amp.id === id);
    const draftIndex = draftAmps.findIndex((amp) => amp.id === id);
    const favoriteIndex = favoriteAmps.findIndex((amp) => amp.id === id);
    const liveIndex = liveAmps.findIndex((amp) => amp.id === id);
    const searchedIndex = searchedAmps.findIndex((amp) => amp.id === id);

    const amp =
      id === state.currentAmp?.id
        ? { ...state.currentAmp }
        : (index !== -1 && { ...amps[index] }) ||
          (draftIndex !== -1 && { ...draftAmps[draftIndex] }) ||
          (favoriteIndex !== -1 && { ...favoriteAmps[favoriteIndex] }) ||
          (liveIndex !== -1 && { ...liveAmps[liveIndex] }) ||
          (searchedIndex !== -1 && { ...searchedAmps[searchedIndex] });

    if (!amp) {
      return;
    }

    updateProperties(amp, data);

    state.createAmpDto = amp;
    state.currentAmp = amp;

    if (index !== -1) {
      amps[index] = amp;
      state.amps = amps;
    }

    if (draftIndex !== -1) {
      draftAmps[draftIndex] = amp;
      state.draftAmps = draftAmps;
    }

    if (favoriteIndex !== -1) {
      favoriteAmps[favoriteIndex] = amp;
      state.favoriteAmps = favoriteAmps;
    }

    if (liveIndex !== -1) {
      liveAmps[liveIndex] = amp;
      state.liveAmps = liveAmps;
    }

    if (searchedIndex !== -1) {
      searchedAmps[searchedIndex] = amp;
      state.searchedAmps = searchedAmps;
    }

    const { organizationId } = amp || {};

    if (organizationId) {
      const recentAmps = [...(state.recentlyViewedAmps[organizationId] || [])];
      const recentlyViewedIndex = recentAmps.findIndex((amp) => amp.id === id);

      if (recentlyViewedIndex !== -1) {
        recentAmps[recentlyViewedIndex] = amp;
        state.recentlyViewedAmps[organizationId] = recentAmps;
      }
    }
  },

  /**
   * Mutation used in deploy and builder stores
   * to update the local state of the @var currentAmp .
   * @param {*} state
   * @param {*} param1
   * @returns
   */
  [UpdateAmpData](state, { conversations, storage, menu }) {
    const conversationsClone = cloneDeep(state.currentAmp.data.conversations || {});
    const storageClone = storage ? { ...storage } : cloneDeep(state.currentAmp.data.storage || {});
    const menuClone = menu ? { ...menu } : cloneDeep(state.currentAmp.data.menu || defaultMenuState);

    if (conversations) {
      conversations.forEach((conversation) => {
        insertElement(conversationsClone, conversation);
      });
    }

    state.currentAmp.data = {
      conversations: conversationsClone,
      storage: storageClone,
      menu: menuClone,
    };

    return state.currentAmp.data;
  },

  [DeleteConversations](state, { conversations }) {
    const conversationsClone = cloneDeep(state.currentAmp.data.conversations);

    conversations.forEach((conversation) => {
      removeElement(conversationsClone, conversation);
    });

    state.currentAmp.data = { conversations: conversationsClone, storage: state.currentAmp.data.storage };
  },

  [SetCreateAmpDto](state, data) {
    state.createAmpDto = data;
  },

  [AddAmpToRecentlyViewed]: (state, amp) => {
    const { recentlyViewedAmps = {} } = state;
    const { organizationId } = amp;

    if (organizationId) {
      const recentAmps = [...(recentlyViewedAmps[organizationId] || [])];
      const index = recentAmps.findIndex((recentAmp) => recentAmp.id === amp.id);

      if (index !== -1) {
        recentAmps.splice(index, 1);
      }

      recentAmps.unshift(amp);

      if (recentAmps.length > RECENTLY_VIEWED_LENGTH) {
        recentAmps.pop();
      }

      state.recentlyViewedAmps = cloneDeep({ ...recentlyViewedAmps, [organizationId]: recentAmps });
    }
  },

  [SetCurrentAmp](state, data) {
    // preserve the avatar preview for AvatarPicker
    if (data.avatarFile && !data.avatarUrl) {
      data.avatarUrl = URL.createObjectURL(data.avatarFile);
    }

    state.currentAmp = data;
    if (!state.draftAmps.length) state.draftAmps.push(data);
    state.createAmpDto = omit(data, ['data', 'id']);
  },

  [SetAmpSortOrder](state, sortOrder) {
    state.sortOrder = sortOrder;
  },
};

export const actions = {
  [ClearStore]({ commit }) {
    commit(ResetStore);
  },

  [ResetMenu]({ commit }) {
    commit(UpdateAmpData, { menu: defaultMenuState });
  },

  async [CreateAmp]({ state, commit, dispatch }) {
    try {
      const { avatarFile, error, ...config } = state.createAmpDto;
      const firstConversation = createConversationObject({ name: 'Default conversation' });

      const ampData = {
        ...config,
        data: {
          conversations:
            state.createAmpDto.ampType === 'edetailer' ? {} : { [firstConversation.id]: firstConversation },
          storage: {},
          menu: defaultMenuState,
        },
        avatarUrl: null,
      };

      const { data } = await this.$amplifyApi.post(endpoints.amp.base, ampData);

      commit(SetCurrentAmp, { ...data, avatarFile });

      if (avatarFile) {
        const fileRes = await dispatch(UploadAmpFile, avatarFile);

        await dispatch(UpdateAmp, { avatarUrl: fileRes?.url || null });
      }

      return data;
    } catch (error) {
      console.error(error);
    }
  },

  async [CopyAmpById]({ commit, dispatch }, id) {
    try {
      const { data } = await this.$amplifyApi.post(endpoints.amp.copy(id));

      dispatch(FetchDraftAmps);

      return data;
    } catch (error) {
      console.error(error);
    }
  },

  async [UpdateAmp]({ state, commit, dispatch }, data) {
    try {
      const { avatarFile, ...newData } = data;

      if (avatarFile) {
        const fileRes = await dispatch(UploadAmpFile, avatarFile);
        newData.avatarUrl = fileRes.url;
      }

      await this.$amplifyApi.patch(endpoints.amp.byId(state.currentAmp.id), newData);

      commit(UpdateAmp, data);
    } catch (error) {
      console.error(error);
    }
  },

  async [GetAmpById]({ commit }, { id, setInitialConversation, setCurrentAmp, updateAmp }) {
    try {
      const { data } = await this.$amplifyApi.get(endpoints.amp.byId(id));

      if (setCurrentAmp) {
        commit(SetCurrentAmp, data);
      }

      if (setInitialConversation) {
        const conversations = objectToSortedArray(data.data.conversations ?? {});
        commit(`builderStore/${SetSelectedConversationId}`, conversations[0]?.id, { root: true });
      }

      if (updateAmp) {
        commit(UpdateAmp, data);
      }

      return data;
    } catch (error) {
      console.error(error);
    }
  },

  async [FetchAmps]({ commit, getters }, opts) {
    const sortBy = getters[GetAmpSortOrder];

    try {
      const params = {
        offset: opts?.offset !== undefined ? opts.offset : 0,
        limit: opts?.limit !== undefined ? opts.limit : 100,
        ...(opts?.query && { query: opts.query }),
        ...(sortBy && { sort: [sortBy.key, sortBy.order].join(':') }),
      };
      const { data } = await this.$amplifyApi.get(endpoints.amp.base, { params });

      commit(SetAmps, data);
    } catch (error) {
      console.error(error);
    }
  },

  async [FetchDraftAmps]({ commit, getters }, opts) {
    const sortBy = getters[GetAmpSortOrder];

    try {
      const params = {
        offset: opts?.offset !== undefined ? opts.offset : 0,
        limit: opts?.limit !== undefined ? opts.limit : 100,
        ...(opts?.query && { query: opts.query }),
        ...(sortBy && { sort: [sortBy.key, sortBy.order].join(':') }),
      };
      const { data } = await this.$amplifyApi.get(endpoints.amp.draft, { params });

      commit(SetDraftAmps, data);
    } catch (error) {
      console.error(error);
    }
  },

  async [FetchTemplates]({ commit }) {
    try {
      const {
        data: { data },
      } = await this.$amplifyApi.get(endpoints.amp.templates);

      commit(SetTemplates, data);
    } catch (error) {
      console.error(error);
    }
  },

  async [GetRecentlyViewedAmps]({
    dispatch,
    state,
    rootGetters: { [`authStore/${GetOrganizationId}`]: organizationId },
  }) {
    try {
      await dispatch(FetchAmps);

      if (organizationId) {
        return (state.recentlyViewedAmps?.[organizationId] || []).reduce((recents, recentAmp) => {
          const foundAmp = state.amps.find((amp) => recentAmp.id === amp.id);

          return foundAmp ? [...recents, foundAmp] : recents;
        }, []);
      }

      return [];
    } catch (err) {
      console.error(err);
    }
  },

  async [DeleteAmpById]({ commit }, id) {
    try {
      await this.$amplifyApi.delete(endpoints.amp.byId(id));

      commit(DeleteAmpById, id);
    } catch (error) {
      console.error(error);
    }
  },

  async [FetchFavoriteAmps]({ commit, getters }, opts) {
    const sortBy = getters[GetAmpSortOrder];

    try {
      const params = {
        offset: opts?.offset !== undefined ? opts.offset : 0,
        limit: opts?.limit !== undefined ? opts.limit : 100,
        ...(opts?.query && { query: opts.query }),
        ...(sortBy && { sort: [sortBy.key, sortBy.order].join(':') }),
      };
      const { data } = await this.$amplifyApi.get(endpoints.amp.favorites, { params });

      commit(SetFavoriteAmps, data);
    } catch (error) {
      console.error(error);
    }
  },

  async [FetchLiveAmps]({ commit, getters }, opts) {
    const sortBy = getters[GetAmpSortOrder];
    try {
      const params = {
        offset: opts?.offset !== undefined ? opts.offset : 0,
        limit: opts?.limit !== undefined ? opts.limit : 100,
        ...(opts?.query && { query: opts.query }),
        ...(sortBy && { sort: [sortBy.key, sortBy.order].join(':') }),
        ...(opts?.sortBy && { sort: [opts.sortBy.key, opts.sortBy.order].join(':') }),
      };
      const { data } = await this.$amplifyApi.get(endpoints.amp.live, { params });

      commit(SetLiveAmps, data);
    } catch (error) {
      console.error(error);
    }
  },

  async [FetchEDetailAmps](_ctx, opts) {
    try {
      const params = {
        offset: opts?.offset || 0,
        limit: opts?.limit || 100,
        filter: 'ampType:edetailer',
        ...(opts?.query && { query: opts.query }),
      };
      const { data } = await this.$amplifyApi.get(endpoints.amp.base, { params });

      return data?.data || [];
    } catch (error) {
      console.error(error);
    }
  },

  [ChangeAmpSortOrder]({ commit, dispatch }, sortOrder) {
    commit(SetAmpSortOrder, sortOrder);
    dispatch(FetchAmps);
  },

  [ChangeDraftAmpSortOrder]({ commit, dispatch }, sortOrder) {
    commit(SetAmpSortOrder, sortOrder);
    dispatch(FetchDraftAmps);
  },

  [ChangeLiveAmpSortOrder]({ commit, dispatch }, sortOrder) {
    commit(SetAmpSortOrder, sortOrder);
    dispatch(FetchLiveAmps);
  },

  [ChangeFavoriteAmpSortOrder]({ commit, dispatch }, sortOrder) {
    commit(SetAmpSortOrder, sortOrder);
    dispatch(FetchFavoriteAmps);
  },

  async [AddToFavorites]({ commit }, ampIds) {
    try {
      await this.$amplifyApi.post(endpoints.amp.addToFavorites, { ampIds });

      ampIds.forEach((id) => commit(UpdateAmp, { id, metadata: { isFavorite: true } }));
    } catch (error) {
      console.error(error);
    }
  },

  async [RemoveFromFavorites]({ state, commit }, ampIds) {
    try {
      await this.$amplifyApi.post(endpoints.amp.removeFromFavorites, { ampIds });

      const updatedAmps = state.favoriteAmps.filter((amp) => !ampIds.includes(amp.id));

      commit(SetFavoriteAmps, { data: updatedAmps, total: state.totalFavoriteAmps - ampIds.length });
      ampIds.forEach((id) => commit(UpdateAmp, { id, metadata: { isFavorite: false } }));
    } catch (error) {
      console.error(error);
    }
  },

  async [SearchAmps]({ commit }, opts) {
    try {
      const params = {
        offset: opts?.offset !== undefined ? opts.offset : 0,
        limit: opts?.limit !== undefined ? opts.limit : 100,
        ...(opts?.query && { query: opts.query }),
      };
      const { data } = await this.$amplifyApi.get(endpoints.amp.base, { params });

      commit(SetSearchedAmps, data);
    } catch (error) {
      console.error(error);
    }
  },

  async [GetPublishedAmp]({ commit }, ampId) {
    try {
      const { data } = await this.$amplifyApi.get(endpoints.amp.published(ampId));

      return data;
    } catch (error) {
      console.error(error);
    }
  },

  /**
   * MEDIA ACTIONS
   * Actions listed for uploading, fetching and
   * updating amp specific media files.
   * @FetchAmpFiles
   * @UploadAmpFile
   * @DeleteAmpFile
   */

  async [FetchAmpFiles]({ getters: { [GetCurrentAmp]: currentAmp } }, { sortBy, filterBy }) {
    const params = {
      ...(sortBy && { sort: [sortBy.key, sortBy.order].join(':') }),
      ...(filterBy?.values?.length && { filter: `${filterBy.key}:` + filterBy.values.join('|') }),
    };

    try {
      const { data } = await this.$amplifyApi.get(endpoints.media.getAll(currentAmp.id), { params });

      return data || {};
    } catch (error) {
      console.error(error);
    }
  },

  async [UploadAmpFile]({ getters: { [GetCurrentAmp]: currentAmp } }, file) {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const { data } = await this.$amplifyApi.post(endpoints.media.base(currentAmp.id), formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      return data;
    } catch (error) {
      const { response: { status } = {} } = error;

      if (status === 413) {
        this.$notification.toast({ name: MediaUploadFailed });
      }

      console.error(error);
    }
  },

  async [DeleteAmpFile]({ getters: { [GetCurrentAmp]: currentAmp } }, fileId) {
    try {
      const { data } = await this.$amplifyApi.delete(endpoints.media.delete(currentAmp.id, fileId));

      return data;
    } catch (error) {
      console.error(error);
    }
  },

  [AddStorageValue]({ commit, getters }, value) {
    let storage = cloneDeep(getters[GetAmpStorage]);
    storage = { [uuidv4()]: value, ...storage };

    commit(UpdateAmpData, { storage });
    commit(UpdateAmp, getters[GetCurrentAmp]);
  },

  [DeleteStorageValue]({ commit, getters }, key) {
    const storage = cloneDeep(getters[GetAmpStorage]);
    delete storage[key];

    commit(UpdateAmpData, { storage });
    commit(UpdateAmp, getters[GetCurrentAmp]);
  },

  [UpdateStorageValue]({ getters, commit, rootGetters }, { key, value }) {
    const storage = cloneDeep(getters[GetAmpStorage]);
    const conversations = objectToSortedArray(cloneDeep(rootGetters[`builderStore/${GetCurrentConversations}`] || {}));

    // Updating storage value
    const prevValue = storage[key];
    const storageObject = { key, value, prevValue };
    storage[key] = value;

    conversations.forEach((conv) => {
      // Replace node content
      Object.values(conv.nodes || {}).forEach((node) => {
        if (!node.type) return;

        // Replacing richText content
        if ([NodeType.message, NodeType.question].includes(node.type)) {
          if (node.html) node.html = replaceStorageValue(node.html, storageObject, true);
        }

        // Replace content in player nodes
        if ([NodeType.player].includes(node.type)) {
          if (node.headline) node.headline = replaceStorageValue(node.headline, storageObject, false);
          if (node.description) node.description = replaceStorageValue(node.description, storageObject, false);
        }

        // Replace content in cards
        if ([NodeType.carousel, NodeType.cardGroup].includes(node.type)) {
          Object.values(node.subNodes).forEach((card) => {
            if (card.headline) card.headline = replaceStorageValue(card.headline, storageObject, false);
            if (card.description) card.description = replaceStorageValue(card.description, storageObject, false);
          });
        }

        // Replacing button text labels
        if ([NodeType.buttonGroup, NodeType.question].includes(node.type)) {
          Object.values(node.subNodes).forEach((button) => {
            button.buttonTexts = button.buttonTexts.map((btnText) =>
              replaceStorageValue(btnText, storageObject, false)
            );
          });
        }
      });

      // Replace trigger content
      Object.values(conv.triggers || {}).forEach((trigger) => {
        if (trigger.compactNudgeMessage) {
          trigger.compactNudgeMessage = replaceStorageValue(trigger.compactNudgeMessage, storageObject, false);
        }
      });
    });

    commit(UpdateAmpData, { storage, conversations });
    commit(UpdateAmp, getters[GetCurrentAmp]);
    commit(`builderStore/${ForceRebuild}`, null, { root: true });
  },
};

export const getters = {
  [GetCurrentAmp]: (state) => {
    return state.currentAmp || {};
  },

  [GetAmpData]: (state) => {
    return state.currentAmp.data || {};
  },

  [GetAmpSortOrder]: (state) => {
    return state.sortOrder;
  },

  [GetCreateAmpDto]: (state) => {
    return state.createAmpDto || {};
  },

  [GetAmpStorage](state) {
    return state.currentAmp?.data?.storage ?? {};
  },

  [GetStorageEntries](_state, { [GetAmpData]: amp, [GetAmpStorage]: storage }, _rootState, rootGetters) {
    const availableAccountKeys = rootGetters[`crmStore/${GetAvailableAccountKeys}`];
    const crmKeys = availableAccountKeys.map((key) => key.name);
    const entries = [...Object.keys(storage), ...crmKeys].reduce(
      (entries, key) => ({ ...entries, [key]: { count: 0 } }),
      {}
    );

    Object.values(amp.conversations).forEach((conv) => {
      Object.values(conv.nodes || {}).forEach((node) => {
        const { storage } = node;

        if (storage) {
          if (entries[storage]) {
            entries[storage].count++;
          } else {
            entries[storage] = { count: 1 };
          }
        }
      });
    });

    return entries;
  },

  [GetStorageKeyValues]:
    (_state, { [GetAmpData]: amp }) =>
    (key) => {
      const conversations = Object.values(amp.conversations);

      const values = conversations.reduce((prev, current) => {
        Object.values(current.nodes || {}).forEach((n) => {
          if (n.storage !== key) return;
          if (n.type === NodeType.question || n.type === NodeType.buttonGroup) {
            prev.push(...buttonsToValues(n.subNodes));
          } else if (n.type === NodeType.plugin || n.type === NodeType.player) {
            prev.push(...['true', 'false']);
          }
        });
        return prev;
      }, []);

      return values
        .reduce((prev, current) => {
          if (!prev.includes(current)) prev.push(current);
          return prev;
        }, [])
        .sort();
    },
};
